import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CostCentreDetail } from "@shared-core/data/manufacturing-setup-types";
import {
  formatChangedInTharstern,
  formatPriceChecksUsedIn,
  formatSyncedFrom,
  siteTownName,
} from "@shared-core/domain/manufacturing-setup-presentation";
import { paths } from "@shared-core/routing/routes";
import { SettingsBreadcrumbs } from "../app/components/patterns/SettingsBreadcrumbs";
import { Badge } from "../app/components/primitives/badge";
import { Tabs, TabsList, TabsTrigger } from "../app/components/primitives/tabs";
import { EngineUseFieldTable } from "../app/components/domain/manufacturing/EngineUseFieldTable";
import { EngineUseRowTable } from "../app/components/domain/manufacturing/EngineUseRowTable";

type SubTab =
  | "rates"
  | "operations"
  | "run-speeds"
  | "setups-runs"
  | "geometry"
  | "packing"
  | "ink-suitability";

const SUB_TABS: { value: SubTab; label: string }[] = [
  { value: "rates", label: "Rates" },
  { value: "operations", label: "Operations" },
  { value: "run-speeds", label: "Run speeds" },
  { value: "setups-runs", label: "Setups & runs" },
  { value: "geometry", label: "Geometry" },
  { value: "packing", label: "Packing" },
  { value: "ink-suitability", label: "Ink suitability" },
];

/** The breadcrumb trail shared by every state of this page — Settings ›
 *  Catalogue › Live costing › Manufacturing setup, plus this cost centre's
 *  own identity once it has loaded. */
function crumbs(leaf?: string) {
  const base = [
    { label: "Settings", to: paths.settingsIntegrations() },
    { label: "Catalogue", to: paths.settingsCatalogues() },
    { label: "Live costing", to: paths.settingsLiveCostingStatus() },
  ];
  return leaf
    ? [
        ...base,
        {
          label: "Manufacturing setup",
          to: paths.settingsLiveCostingManufacturingSetup(),
        },
        { label: leaf },
      ]
    : [...base, { label: "Manufacturing setup" }];
}

export function ManufacturingSetupCostCentreDetailPage() {
  const adapter = useDataAdapter();
  const { costCentreId: costCentreIdParam } = useParams<{
    costCentreId: string;
  }>();
  const costCentreId = costCentreIdParam ? Number(costCentreIdParam) : NaN;
  const [searchParams, setSearchParams] = useSearchParams();
  const subTab = (searchParams.get("tab") as SubTab) || "rates";

  const [detail, setDetail] = useState<CostCentreDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    if (Number.isNaN(costCentreId)) return;
    setLoadError(false);
    adapter
      .getManufacturingCostCentre(costCentreId)
      .then((d) => {
        if (d === null) {
          setNotFound(true);
          return;
        }
        setDetail(d);
      })
      .catch(() => setLoadError(true));
  }, [adapter, costCentreId]);

  useEffect(() => {
    load();
  }, [load]);

  if (Number.isNaN(costCentreId) || notFound) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">
          No such cost centre. The link may be out of date.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (loadError) {
    return (
      <BreadcrumbsOnly>
        <p className="text-destructive">
          Couldn&rsquo;t load this cost centre. Please try again.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (!detail) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">Loading...</p>
      </BreadcrumbsOnly>
    );
  }

  return (
    <div className="space-y-4">
      <SettingsBreadcrumbs
        crumbs={crumbs(`${detail.costCentreId} ${detail.code}`)}
      />

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-foreground">
          Cost centre {detail.costCentreId} · {detail.code}
          {detail.name !== detail.code && (
            <span className="ml-2 text-lg font-normal text-muted-foreground">
              — {detail.name}
            </span>
          )}
        </h1>
        <p className="text-sm text-muted-foreground">
          {detail.processTypeName} · {siteTownName(detail.site)} ·{" "}
          <Badge variant={detail.isAvailable ? "success" : "neutral"}>
            {detail.isAvailable ? "Available" : "Unavailable"}
          </Badge>
        </p>
        <p className="text-sm text-muted-foreground">
          {formatChangedInTharstern(detail.changedInTharsternAt)}
        </p>
        <p className="text-sm">
          {detail.priceChecksUsedIn === 0 ? (
            <span className="text-muted-foreground">
              {formatPriceChecksUsedIn(0)}
            </span>
          ) : (
            <Link
              to={paths.settingsLiveCostingChecks({
                view: "cost-centre",
                costCentreId: detail.costCentreId,
              })}
              className="text-[color:var(--color-primary)] hover:underline"
            >
              {formatPriceChecksUsedIn(detail.priceChecksUsedIn)}
            </Link>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatSyncedFrom(detail.sync)}
        </p>
      </div>

      <Tabs
        value={subTab}
        onValueChange={(v) =>
          setSearchParams(
            (prev) => {
              const params = new URLSearchParams(prev);
              if (v === "rates") params.delete("tab");
              else params.set("tab", v);
              return params;
            },
            { replace: true },
          )
        }
      >
        <TabsList>
          {SUB_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {subTab === "rates" && (
        <EngineUseFieldTable
          fields={detail.rates}
          emptyMessage="No rates captured for this cost centre."
        />
      )}
      {subTab === "operations" && (
        <EngineUseRowTable
          rows={detail.operations}
          emptyMessage="No operation rows captured for this cost centre."
        />
      )}
      {subTab === "run-speeds" && (
        <EngineUseRowTable
          rows={detail.runSpeeds}
          emptyMessage="No run-speed bands captured for this cost centre."
        />
      )}
      {subTab === "setups-runs" && (
        <div className="space-y-4">
          <EngineUseRowTable
            rows={detail.setups}
            emptyMessage="No setup rows captured for this cost centre."
          />
          <EngineUseRowTable
            rows={detail.runs}
            emptyMessage="No run rows captured for this cost centre."
          />
        </div>
      )}
      {subTab === "geometry" && (
        <EngineUseRowTable
          rows={detail.geometry}
          emptyMessage="No press geometry captured for this cost centre."
        />
      )}
      {subTab === "packing" && (
        <EngineUseRowTable
          rows={detail.packing}
          emptyMessage="No packing attributes captured for this cost centre."
        />
      )}
      {subTab === "ink-suitability" && (
        <InkSuitabilityTable inkSuitability={detail.inkSuitability} />
      )}
    </div>
  );
}

function InkSuitabilityTable({
  inkSuitability,
}: {
  inkSuitability: CostCentreDetail["inkSuitability"];
}) {
  if (inkSuitability.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
        Not a printing cost centre — no ink suitability recorded.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm">
        <tbody className="divide-y divide-border">
          {inkSuitability.map((ink) => (
            <tr key={ink.inkProfileId}>
              <td className="px-3 py-2 font-medium">{ink.code}</td>
              <td className="px-3 py-2 text-muted-foreground">{ink.name}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BreadcrumbsOnly({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <SettingsBreadcrumbs crumbs={crumbs()} />
      {children}
    </div>
  );
}
